import "./AuthoringSuccessPage.scss";

import { Button, Flex, Text } from "@fluentui/react-northstar";
import { dialog } from "@microsoft/teams-js";
import { getSurveyTypeDisplay } from "@teams/cards";
import { canCreateWebShareForPolly } from "@teams/client-shared";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";

import { useSurvey } from "../../../_shared/actions/useSurvey";
import SuccessfulSubmission from "../../../_shared/assets/confirmation-image-ok-mark.svg";
import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { SurveyExternalLink } from "../../../_shared/components/SurveyExternalLink";
import { TeamsLoader } from "../../../_shared/components/TeamsLoader";
import { isError } from "../../../_shared/services/ChannelApi";
import { userApi } from "../../../_shared/services/UserApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { getPageContext } from "../../../_shared/utils/teamsHelpers";

export const AuthoringSuccessPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { teamsContext } = useTeamsContext();
  const isMeetingContext = !!teamsContext.meeting?.id;
  const history = useHistory();

  const { search } = useLocation();

  const urlParms = new URLSearchParams(search);
  const update = urlParms.get("update");
  const recurring = urlParms.get("recurring");

  const { survey, loadSurvey, isSurveyLoading } = useSurvey();

  const [linkCreated, setLinkCreated] = useState<boolean>();
  const [surveyCount, setSurveyCount] = useState<Number | null | undefined>();
  const [firstPollyOffset, setFirstPollyOffset] = useState<number>(0);

  useEffect(() => {
    setLinkCreated(!!survey?.shortCodeUri);
    if (!!survey?.shortCodeUri) {
      dialog.update.resize({ width: 550, height: 315 + firstPollyOffset });
    } else {
      dialog.update.resize({ width: 550, height: 230 + firstPollyOffset });
    }
  }, [firstPollyOffset, survey?.shortCodeUri]);

  useEffect(() => {
    if (recurring) {
      return;
    }
    loadSurvey(teamsContext, surveyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, teamsContext, recurring]);

  useEffect(() => {
    (async () => {
      const count = await userApi.getSurveyCountByOwner(teamsContext);
      if (isError(count)) {
        setSurveyCount(null);
      } else {
        setSurveyCount(count);
        if (count === 1) {
          setFirstPollyOffset(50);
        }
      }
    })();
  }, [teamsContext]);

  const handleCreateAnother = useCallback(() => {
    dialog.update.resize({ width: 700, height: 650 });

    const context = getPageContext();
    let url = `${context}/content/start`;

    history.replace(url);
  }, [history]);

  const scheduled = useMemo(() => {
    if (!survey) {
      return;
    }
    return moment(survey.deliverAt).diff(new Date(), "seconds") > 30;
  }, [survey]);

  const primaryText = useMemo(() => {
    if (!recurring && !survey) {
      return;
    }

    if (update) {
      if (recurring) {
        return "Series updated!";
      }
      return `${getSurveyTypeDisplay(survey!.type, { capitalize: true })} updated!`;
    }

    if (surveyCount === 1) {
      return (
        <>
          Congratulations on {scheduled ? "scheduling" : "sending"} your first{" "}
          {recurring ? "series" : `${getSurveyTypeDisplay(survey!.type)}`}! Create your next polly
          using one of our
          <Link className="brand-link" to={"/msteams/content/templates?fromSuccessPage=true"}>
            {" "}
            templates.{" "}
          </Link>
        </>
      );
    }

    if (recurring) {
      return "New series created!";
    }

    if (scheduled) {
      return `${getSurveyTypeDisplay(survey!.type, { capitalize: true })} scheduled!`;
    }

    return `New ${getSurveyTypeDisplay(survey!.type)} created!`;
  }, [recurring, scheduled, survey, surveyCount, update]);
  return (
    <div className="authoring-success-page">
      <TeamsLoader
        loadingMessage="Loading polly..."
        isLoading={(!recurring && isSurveyLoading) || surveyCount === undefined}
      >
        <Flex fill column gap="gap.small" hAlign="start">
          {(survey || recurring) && (
            <>
              <Flex fill column hAlign="center" gap="gap.small">
                <img src={SuccessfulSubmission} height={80} alt="Success" />
                <Text size="large" weight="semibold" align="center">
                  {primaryText}
                </Text>
                {scheduled && (
                  <Text
                    content={`This ${getSurveyTypeDisplay(
                      survey!.type
                    )} will be delivered on ${moment(survey?.deliverAt).format(
                      "MMM DD, YYYY - hh:mm A"
                    )}`}
                    styles={{ marginTop: surveyCount ?? 2 > 1 ? "10px" : "20px" }}
                  />
                )}
              </Flex>

              {isMeetingContext && (
                <Flex.Item align="center">
                  <Button
                    className="create-another-button"
                    key="create"
                    primary={true}
                    onClick={handleCreateAnother}
                    content={
                      <PollyIcon icon="create-icon" label="Create Another Polly" inheritColor />
                    }
                  />
                </Flex.Item>
              )}

              {survey && canCreateWebShareForPolly(survey.type) && !recurring && (
                <>
                  <Flex.Item>
                    <Flex
                      vAlign="center"
                      column={!!linkCreated}
                      className="authoring-success-page__footer"
                    >
                      <Flex vAlign="center" gap="gap.smaller" className="share-message">
                        <PollyIcon icon="info-icon" inheritColor />
                        <Text>Share this Polly with anyone in the company.</Text>
                      </Flex>
                      <SurveyExternalLink
                        survey={survey}
                        trackingMethod="authoring-success-page"
                        onChanged={() => {
                          loadSurvey(teamsContext, surveyId, { hideLoader: true });
                          setLinkCreated(true);
                          dialog.update.resize({ width: 550, height: 315 });
                        }}
                      />
                      <Flex.Item align="end">
                        <Button
                          content={"Done"}
                          size="small"
                          className="done-button"
                          styles={!!linkCreated ? { marginTop: "30px" } : {}}
                          onClick={() => {
                            dialog.url.submit();
                          }}
                        />
                      </Flex.Item>
                    </Flex>
                  </Flex.Item>
                </>
              )}
              {recurring && (
                <>
                  <Flex.Item>
                    <Flex.Item align="end">
                      <Button
                        content={"Done"}
                        size="small"
                        className="done-button"
                        onClick={() => {
                          dialog.url.submit();
                        }}
                      />
                    </Flex.Item>
                  </Flex.Item>
                </>
              )}
            </>
          )}
        </Flex>
      </TeamsLoader>
    </div>
  );
};
